import { getAccountSummary } from './account-module';
import { setPendingMeds } from './my-meds-module';
import { isDesktop } from './header-module';
import type {
  ChatDetails,
  ChatTokenAPIResponse,
  FulfillmentOption,
  FulfillmentOptionName,
  FulfillmentSelection,
  Item,
  Order,
  Patient,
  Prescription,
  QuestionResponse,
  SplashCopy,
  Survey,
  InsurancePayload,
} from './reverie-api.types';
import type { APIError, GetState, ThunkAction, ThunkExtras, Dispatch } from '../../types/app.types';
import { trackEvent } from '../../utils/analytics';
declare global {
  interface Window {
    Kustomer: any;
  }
}
/***************
 * Action Types *
 ***************/
type SetInitialized = {
  type: 'reverie/SET_INITIALIZED';
};
type SetFetching = {
  type: 'reverie/SET_FETCHING';
  fetching: boolean;
};
type SetPatient = {
  type: 'reverie/SET_PATIENT';
  patient: Patient;
};
type SetPrescriptions = {
  type: 'reverie/SET_PRESCRIPTIONS';
  prescriptions: Array<Prescription>;
};
type SetSplashCopy = {
  type: 'reverie/SET_SPLASH_COPY';
  splashCopy: SplashCopy;
};
type SetOrder = {
  type: 'reverie/SET_ORDER';
  order: Order;
};
type SetPreviousOrder = {
  type: 'reverie/SET_PREVIOUS_ORDER';
  previousOrders: Array<Order>;
};
type GetFulfillmentOptionsSuccess = {
  type: 'reverie/GET_FULFILLMENT_OPTIONS_SUCCESS';
};
type SetSelectedFulfillmentOptionSuccess = {
  type: 'reverie/SET_SELECTED_FULFILLMENT_OPTION_SUCCESS';
  fulfillmentSelection: FulfillmentSelection;
};
type SetChatStarted = {
  type: 'reverie/SET_CHAT_STARTED';
  started: boolean;
};
type SetChatToken = {
  type: 'reverie/SET_CHAT_TOKEN';
  token: string;
  expires: number;
};
type SetChatProfile = {
  type: 'reverie/SET_CHAT_PROFILE';
};
type SetChatFetching = {
  type: 'reverie/SET_CHAT_FETCHING';
};
type AddItemSuccess = {
  type: 'reverie/ADD_ITEM_SUCCESS';
  item: Item;
  price: number;
};
type RemoveItemSuccess = {
  type: 'reverie/REMOVE_ITEM_SUCCESS';
  itemId: string;
  price: number;
};
type SelectPriceTypeSuccess = {
  type: 'reverie/SELECT_PRICE_TYPE_SUCCESS';
  itemId: string;
  newItem: Item;
  priceDiff: number;
};
type SubmitOrderSuccess = {
  type: 'reverie/SUBMIT_ORDER_SUCCESS';
  cardId: string | null | undefined;
};
type SetOrderSpecialInstructionsSuccess = {
  type: 'reverie/SET_ORDER_SPECIAL_INSTRUCTIONS_SUCCESS';
  specialInstructions: string | null | undefined;
};
type SetAutoRefillOptedInSuccess = {
  type: 'reverie/SET_AUTO_REFILL_OPTED_IN_SUCCESS';
  itemId: string;
  autoRefillOptedIn: boolean;
};
type CancelAutoRefillSuccess = {
  type: 'reverie/CANCEL_AUTO_REFILL_SUCCESS';
  medNameId: string;
};
type DelayAutoRefillSuccess = {
  type: 'reverie/DELAY_AUTO_REFILL_SUCCESS';
  medNameId: string;
  expectedDate: Date;
};
type GetExitTokenSuccess = {
  type: 'reverie/GET_EXIT_TOKEN_SUCCESS';
};
type SubmitInsuranceInfoSuccess = {
  type: 'reverie/SUBMIT_INSURANCE_INFO_SUCCESS';
};
type VerifyPatientSuccess = {
  type: 'reverie/VERIFY_PATIENT_SUCCESS';
};
type SendVerifyCodeSuccess = {
  type: 'reverie/SEND_VERIFY_CODE_SUCCESS';
};
type CancelItemSuccess = {
  type: 'reverie/CANCEL_ITEM_SUCCESS';
  orderId: string;
  itemId: string;
};
type SetSurveys = {
  type: 'reverie/SET_SURVEYS';
  surveys: Array<Survey>;
};
type SubmitQuestionResponseSuccess = {
  type: 'reverie/SUBMIT_QUESTION_RESPONSE_SUCCESS';
  questionId: string;
  surveyId: string;
  value: string;
};
type SubmitSurveyResponseSuccess = {
  type: 'reverie/SUBMIT_SURVEY_RESPONSE_SUCCESS';
};
export type ReverieAction =
  | SetInitialized
  | SetPatient
  | SetPrescriptions
  | SetSplashCopy
  | SetOrder
  | SetPreviousOrder
  | SetFetching
  | AddItemSuccess
  | RemoveItemSuccess
  | SelectPriceTypeSuccess
  | GetFulfillmentOptionsSuccess
  | SetSelectedFulfillmentOptionSuccess
  | SetOrderSpecialInstructionsSuccess
  | SubmitOrderSuccess
  | SetAutoRefillOptedInSuccess
  | CancelAutoRefillSuccess
  | DelayAutoRefillSuccess
  | SetChatStarted
  | SetChatToken
  | SetChatProfile
  | SetChatFetching
  | GetExitTokenSuccess
  | SubmitInsuranceInfoSuccess
  | VerifyPatientSuccess
  | SendVerifyCodeSuccess
  | CancelItemSuccess
  | SetSurveys
  | SubmitQuestionResponseSuccess
  | SubmitSurveyResponseSuccess;

/*****************
 * (Pure) Actions *
 *****************/
function setInitialized(): SetInitialized {
  return {
    type: 'reverie/SET_INITIALIZED',
  };
}

function setFetching(fetching = true): SetFetching {
  return {
    type: 'reverie/SET_FETCHING',
    fetching,
  };
}

function setOrder(order: Order): SetOrder {
  return {
    type: 'reverie/SET_ORDER',
    order,
  };
}

function setPatient(patient: Patient): SetPatient {
  return {
    type: 'reverie/SET_PATIENT',
    patient,
  };
}

function setSplashCopy(splashCopy: SplashCopy): SetSplashCopy {
  return {
    type: 'reverie/SET_SPLASH_COPY',
    splashCopy,
  };
}

function setPrescriptions(prescriptions: Array<Prescription>): SetPrescriptions {
  return {
    type: 'reverie/SET_PRESCRIPTIONS',
    prescriptions,
  };
}

function setPreviousOrder(previousOrders: Array<Order>): SetPreviousOrder {
  return {
    type: 'reverie/SET_PREVIOUS_ORDER',
    previousOrders,
  };
}

function addItemSuccess(item: Item, price: number): AddItemSuccess {
  return {
    type: 'reverie/ADD_ITEM_SUCCESS',
    item,
    price,
  };
}

function removeItemSuccess(itemId: string, price: number): RemoveItemSuccess {
  return {
    type: 'reverie/REMOVE_ITEM_SUCCESS',
    itemId,
    price,
  };
}

function selectPriceTypeSuccess(itemId: string, newItem: Item, priceDiff: number): SelectPriceTypeSuccess {
  return {
    type: 'reverie/SELECT_PRICE_TYPE_SUCCESS',
    itemId,
    newItem,
    priceDiff,
  };
}

function getFulfillmentOptionsSuccess(): GetFulfillmentOptionsSuccess {
  return {
    type: 'reverie/GET_FULFILLMENT_OPTIONS_SUCCESS',
  };
}

function setSelectedFulfillmentOptionSuccess(
  fulfillmentSelection: FulfillmentSelection
): SetSelectedFulfillmentOptionSuccess {
  return {
    type: 'reverie/SET_SELECTED_FULFILLMENT_OPTION_SUCCESS',
    fulfillmentSelection,
  };
}

function setOrderSpecialInstructionsSuccess(
  specialInstructions: string | null | undefined
): SetOrderSpecialInstructionsSuccess {
  return {
    type: 'reverie/SET_ORDER_SPECIAL_INSTRUCTIONS_SUCCESS',
    specialInstructions,
  };
}

function setAutoRefillOptedInSuccess(itemId: string, autoRefillOptedIn: boolean): SetAutoRefillOptedInSuccess {
  return {
    type: 'reverie/SET_AUTO_REFILL_OPTED_IN_SUCCESS',
    itemId,
    autoRefillOptedIn,
  };
}

function cancelAutoRefillSuccess(medNameId: string): CancelAutoRefillSuccess {
  return {
    type: 'reverie/CANCEL_AUTO_REFILL_SUCCESS',
    medNameId,
  };
}

function delayAutoRefillSuccess(medNameId: string, expectedDate: Date): DelayAutoRefillSuccess {
  return {
    type: 'reverie/DELAY_AUTO_REFILL_SUCCESS',
    medNameId,
    expectedDate,
  };
}

function submitOrderSuccess(cardId: string | null | undefined): SubmitOrderSuccess {
  return {
    type: 'reverie/SUBMIT_ORDER_SUCCESS',
    cardId,
  };
}

function verifyPatientSuccess(): VerifyPatientSuccess {
  return {
    type: 'reverie/VERIFY_PATIENT_SUCCESS',
  };
}

function getExitTokenSuccess(): GetExitTokenSuccess {
  return {
    type: 'reverie/GET_EXIT_TOKEN_SUCCESS',
  };
}

function submitInsuranceInfoSuccess(): SubmitInsuranceInfoSuccess {
  return {
    type: 'reverie/SUBMIT_INSURANCE_INFO_SUCCESS',
  };
}

function sendVerifyCodeSuccess(): SendVerifyCodeSuccess {
  return {
    type: 'reverie/SEND_VERIFY_CODE_SUCCESS',
  };
}

function setChatToken(token: string, expires: number): SetChatToken {
  return {
    type: 'reverie/SET_CHAT_TOKEN',
    token,
    expires,
  };
}
function setChatFetching(): SetChatFetching {
  return {
    type: 'reverie/SET_CHAT_FETCHING',
  };
}

export function setChatStarted(): SetChatStarted {
  return {
    type: 'reverie/SET_CHAT_STARTED',
    started: true,
  };
}

function cancelItemSuccess(orderId: string, itemId: string): CancelItemSuccess {
  return {
    type: 'reverie/CANCEL_ITEM_SUCCESS',
    orderId,
    itemId,
  };
}

function setSurveys(surveys: Array<Survey>): SetSurveys {
  return {
    type: 'reverie/SET_SURVEYS',
    surveys,
  };
}

function submitQuestionResponseSuccess(
  surveyId: string,
  questionId: string,
  value: string
): SubmitQuestionResponseSuccess {
  return {
    type: 'reverie/SUBMIT_QUESTION_RESPONSE_SUCCESS',
    surveyId,
    questionId,
    value,
  };
}

function submitSurveyResponseSuccess(): SubmitSurveyResponseSuccess {
  return {
    type: 'reverie/SUBMIT_SURVEY_RESPONSE_SUCCESS',
  };
}

/************************
 * (Async) Thunk Actions *
 ************************/
export function initReverieStore(patientId: string): ThunkAction<Promise<any>> {
  return function (dispatch): Promise<any> {
    dispatch(setFetching());
    return dispatch(getPatient(patientId))
      .then(() => dispatch(getPrescriptions(patientId)))
      .then(() => dispatch(openOrder(patientId)))
      .then(() => dispatch(getPreviousOrders(patientId)))
      .then(() => dispatch(setInitialized()));
  };
}
export function signup(email: string, password?: string, skipEmail?: boolean): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    const signupEndPoint = '/v2/reverie/api/user/account/signup';
    return extras.blinkAPIClient
      .post(signupEndPoint, {
        email,
        password,
        skipEmail,
        acceptsAgreements: true,
        rememberMe: true,
      })
      .then(() => {
        dispatch(
          trackEvent('Created Account', {
            type: 'guest',
          })
        );
        return dispatch(getAccountSummary());
      })
      .catch((err: APIError) => {
        throw err;
      });
  };
}

function getPatient(patientId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/reverie/api/v1/patients/${patientId}/`, {
        params: {
          ignore_pharmacy: true,
        },
      })
      .then((res) => res.data)
      .then((res: Patient) => dispatch(setPatient(res)));
  };
}

export function getRxosPrescriptions(exitToken: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient.get(`/reverie/api/v2/exit_token/prescriptions/${exitToken}/`).then((res) => {
      const { nonHubScripts, prescriptions, splashCopy } = res.data;
      if (nonHubScripts) dispatch(setPendingMeds(nonHubScripts));
      dispatch(setSplashCopy(splashCopy));
      dispatch(setPrescriptions(prescriptions));
      return Promise.resolve();
    });
  };
}
export function getOrder(orderId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/reverie/api/v1/oms/orders/${orderId}/`)
      .then((res) => res.data)
      .then((res: Order) => dispatch(setOrder(res)));
  };
}
export function openOrder(patientId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/reverie/api/v1/patients/${patientId}/open_order/?order_type=reverie`)
      .then((res) => res.data)
      .then((res: Order) => dispatch(setOrder(res)));
  };
}
export function getPreviousOrders(patientId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/reverie/api/v1/patients/${patientId}/past_orders/`)
      .then((res) => res.data.orders)
      .then((res: Array<Order>) => dispatch(setPreviousOrder(res)));
  };
}
export function addItem(orderId: string, rxosPrescriptionId: string, priceKey: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient // TODO: update the url
      .post(`/reverie/api/v2/oms/orders/${orderId}/items/`, {
        prescriptionId: rxosPrescriptionId,
        key: priceKey,
      })
      .then((res) => res.data)
      .then((res: Order) => {
        const item = res.items.find((i) => i.detail.rxosPrescriptionId === rxosPrescriptionId);

        if (item) {
          const price = item.prices.find((p) => p.id === item.selectedPriceId);
          if (!price) throw new Error('Price must be present.');
          return dispatch(addItemSuccess(item, price.price));
        } else {
          throw new Error(`Cannot add item for ${rxosPrescriptionId}.`);
        }
      })
      .catch((error: APIError) => {
        throw error;
      });
  };
}
export function removeItem(orderId: string, itemId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient.delete(`/reverie/api/v1/oms/orders/${orderId}/items/${itemId}/`).then((res) => {
      const order = getState().reverie.order;
      if (!order) throw new Error('An order must be present.');
      const origItem = order.items.find((i) => i.id === itemId);
      if (!origItem) throw new Error('Original item must be present');
      const price = origItem.prices.find((p) => p.id === origItem.selectedPriceId);
      if (!price) throw new Error('Price must be present.');
      if (
        res.status !== 204
        /* No Content */
      )
        throw new Error('Unable to remove item from order; please try again');
      return dispatch(removeItemSuccess(itemId, price.price));
    });
  };
}
export function selectPriceType(orderId: string, itemId: string, priceId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .put(`/reverie/api/v1/oms/orders/${orderId}/items/${itemId}/select_price/`, {
        selectedPriceId: priceId,
      })
      .then((res) => res.data)
      .then((newOrder) => {
        const order = getState().reverie.order;
        if (!order) throw new Error('An order must be present.');
        const origItem = order.items.find((i) => i.id === itemId);
        if (!origItem) throw new Error('Original item must be present');
        const newItem = newOrder.items.find((i) => i.id === itemId);
        if (!newItem) throw new Error('New item must be present');
        const origPrice = origItem.prices.find((p) => p.id === origItem.selectedPriceId);
        const newPrice = origItem.prices.find((p) => p.id === priceId);
        if (!(origPrice && newPrice)) throw new Error('Prices must be present.');
        return dispatch(selectPriceTypeSuccess(itemId, newItem, newPrice.price - origPrice.price));
      });
  };
}
export function getFulfillmentOptions(orderId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient.get(`/reverie/api/v1/oms/orders/${orderId}/fulfillment_options/`).then((res) => {
      dispatch(getFulfillmentOptionsSuccess());
      return res.data;
    });
  };
}
export function setSelectedFulfillmentOption(
  orderId: string,
  fulfillmentOption: FulfillmentOption,
  selectedFulfillmentOption: FulfillmentOptionName,
  signatureOptedIn: boolean
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .post(`/reverie/api/v1/oms/orders/${orderId}/fulfillment_options/`, {
        selectedFulfillmentOption,
        id: fulfillmentOption.id,
        signatureOptedIn,
      })
      .then((res) => {
        if (res.status !== 201) throw new Error('Unable to select this fulfillment option; please try again.');
        const fulfillmentSelection: FulfillmentSelection = {
          id: fulfillmentOption.id,
          price: fulfillmentOption.price,
          displayName: fulfillmentOption.displayName,
          fulfillmentType: fulfillmentOption.fulfillmentType,
          signatureOptedIn: signatureOptedIn,
        };
        dispatch(setSelectedFulfillmentOptionSuccess(fulfillmentSelection));
      });
  };
}
export function setOrderSpecialInstructions(
  orderId: string,
  specialInstructions: string | null | undefined
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .patch(`/reverie/api/v1/oms/orders/${orderId}/`, {
        specialInstructions,
      })
      .then((res) => res.data)
      .then(() => dispatch(setOrderSpecialInstructionsSuccess(specialInstructions)));
  };
}
export function setAutoRefillOptedIn(
  orderId: string,
  itemId: string,
  autoRefillOptedIn: boolean
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .put(`/reverie/api/v1/oms/orders/${orderId}/items/${itemId}/auto_refill/`, {
        autoRefillOptedIn,
      })
      .then((res) => res.data)
      .then(() => dispatch(setAutoRefillOptedInSuccess(itemId, autoRefillOptedIn)));
  };
}
export function submitOrder(orderId: string, cardId: string | null | undefined): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    const c_platform = isDesktop(getState()) ? 'web' : 'mobile_web';
    return blinkAPIClient
      .post(
        `/reverie/api/v1/oms/orders/${orderId}/submit/`,
        {
          cardId,
        },
        {
          params: {
            c_platform,
          },
        }
      )
      .then((res) => res.data)
      .then(() => dispatch(submitOrderSuccess(cardId)))
      .catch((err) => {
        if (err.status === 422) {
          dispatch(setFetching(false));
        }

        throw err;
      });
  };
}
export function cancelAutoRefill(
  patientId: string,
  medNameId: string,
  code: string,
  otherReason: string
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .post(`/reverie/api/v1/patients/${patientId}/cancel_auto_refill/`, {
        medNameId,
        reason: {
          code,
          full_reason: otherReason,
        },
      })
      .then((res) => {
        return dispatch(cancelAutoRefillSuccess(res.data.medNameId));
      });
  };
}
export function delayAutoRefill(
  patientId: string,
  medNameId: string,
  expectedDate: Date,
  code?: string,
  otherReason?: string | null | undefined
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .put(`/reverie/api/v1/patients/${patientId}/auto_refill/${medNameId}/date/`, {
        expectedDate: expectedDate.toISOString(),
        reason: code
          ? {
              code,
              content: otherReason,
            }
          : null,
      })
      .then(() => {
        return dispatch(getPrescriptions(patientId)).then(() =>
          dispatch(delayAutoRefillSuccess(medNameId, expectedDate))
        );
      });
  };
}
export function getExitToken(lastName: string, dob: string, phoneNumber: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .post('/reverie/api/patient/verify/', {
        lastName,
        dob,
        phoneNumber,
      })
      .then((res) => res.data.result)
      .then((res) => {
        dispatch(getExitTokenSuccess());
        return {
          exitToken: res.exitToken,
          isHubRx: res.isHubRx,
          flow: res.flow,
        };
      });
  };
}
export function submitInsuranceInfo(payload: InsurancePayload, exitToken: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .post(`/reverie/api/patient/insurance/${exitToken}`, payload)
      .then((res) => res.data.result)
      .then(() => {
        dispatch(submitInsuranceInfoSuccess());
      })
      .catch((err) => {
        throw err;
      });
  };
}
export function verifyPatient(exitToken: string, verifyCode: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .put('/reverie/api/patient/verify/', {
        exitToken,
        verifyCode,
      })
      .then((res) => res.data.result)
      .then(() => dispatch(verifyPatientSuccess()))
      .catch((err) => {
        //This error will get caught by the component
        throw new Error(err);
      });
  };
}
export function sendVerifyCodeSMS(exitToken: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .get(`/reverie/api/patient/verify/?exit_token=${exitToken}`)
      .then((res) => res.data.result)
      .then(() => dispatch(sendVerifyCodeSuccess()))
      .catch((err) => {
        throw err;
      });
  };
}
export function getChatToken(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get('/reverie/api/chat/identify/')
      .then((res) => res.data.result)
      .then((res: ChatTokenAPIResponse) => {
        dispatch(setChatToken(res.token, res.expires));
        return {
          token: res.token,
          expires: res.expires,
        };
      });
  };
}
//This can only be used by authenticated reverie users
export function getChatProfile(): ThunkAction<Promise<any>> {
  return async function (dispatch, getState): Promise<any> {
    const appState = getState();
    const { Kustomer } = window;

    if (Kustomer) {
      let { chat } = appState.reverie;

      if (!chat.token) {
        chat = await dispatch(getChatToken());
      }

      const { expires } = chat;
      const timeInMilliSeconds = new Date().getTime();

      //Python uses seconds
      if (expires && timeInMilliSeconds > expires * 1000) {
        chat = await dispatch(getChatToken());
      }

      return chat.token;
    }
  };
}
export function startChatSession(): ThunkAction<Promise<any>> {
  return async function (dispatch, getState): Promise<any> {
    const { isReverieUser } = getState().account;
    const { Kustomer } = window;

    if (Kustomer) {
      let jwtToken;
      await dispatch(setChatFetching());

      if (isReverieUser) {
        jwtToken = await dispatch(getChatProfile());
      }

      Kustomer.start({}, () => {
        Kustomer.open();
        if (jwtToken) {
          Kustomer.login({ jwtToken });
        }
      });
    }
  };
}
export function cancelItem(orderId: string, itemId: string): ThunkAction<Promise<any>> {
  return async function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .post(`/reverie/api/v1/oms/orders/${orderId}/items/${itemId}/cancel_item/`)
      .then((res) => res.data.result)
      .then(() => dispatch(cancelItemSuccess(orderId, itemId)));
  };
}
export function getPrescriptions(patientId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient // NOTE: ?blink_account_id is automatically appended in ecomm Backend
      .get(`/reverie/api/v2/patients/prescriptions/`, {
        params: {
          blink_account_id: patientId,
        },
      })
      .then((res) => {
        const { nonHubScripts, prescriptions, splashCopy } = res.data;
        if (nonHubScripts) dispatch(setPendingMeds(nonHubScripts));
        dispatch(setSplashCopy(splashCopy));
        dispatch(setPrescriptions(prescriptions));
        return Promise.resolve();
      });
  };
}
export function getSurveys(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const { id } = getState().account;
    return blinkAPIClient // NOTE: ?blink_account_id is automatically appended in ecomm Backend
      .get(`/reverie/api/v1/surveys/`, {
        params: {
          blink_account_id: id,
        },
      })
      .then((res) => {
        const { surveys } = res.data;
        //TODO(ABA): Add hideNumber field for conditional questions. Move to Socrates
        const formattedSurveys = surveys.map((survey) => {
          survey.pages[0].elements.map((q) => {
            if (q.visibleIf) {
              q.hideNumber = true;
            }

            if (q.type === 'text' && q.inputType === 'text') q.type = 'comment';
            return q;
          });
          return survey;
        });
        dispatch(setSurveys(formattedSurveys));
        return formattedSurveys;
      });
  };
}
export function submitQuestionResponse(surveyId: string, questionId: string, value: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const { id } = getState().account;
    return blinkAPIClient
      .put(`/reverie/api/v1/users/${id}/surveys/${surveyId}/questions/${questionId}/responses/`, {
        value,
      })
      .then((res) => {
        dispatch(submitQuestionResponseSuccess(surveyId, questionId, value));
        return res.data;
      });
  };
}
export function submitSurveyResponse(surveyId: string, responses: Array<QuestionResponse>): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const { id } = getState().account;
    return blinkAPIClient
      .put(`/reverie/api/v1/users/${id}/surveys/${surveyId}/responses/`, {
        responses: responses,
      })
      .then(() => dispatch(submitSurveyResponseSuccess()));
  };
}
export function verifyCloudPatient(exitToken: string, email: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .post(`/reverie/api/patient/organic_referrals/`, {
        exitToken: exitToken,
        email: email,
      })
      .then((res) => res.data.result)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };
}
export function updateCloudPatientAccount(exitToken: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    return blinkAPIClient
      .put(`/reverie/api/patient/organic_referrals/`, {
        exitToken: exitToken,
      })
      .then((res) => res.data.result)
      .then(() => {
        return dispatch(getAccountSummary());
      });
  };
}

/**********
 * Reducer*
 **********/
export type ReverieState = {
  initialized: boolean;
  fetching: boolean;
  patient: Patient | null | undefined;
  prescriptions: Array<Prescription>;
  splashCopy: SplashCopy | null | undefined;
  order: Order | null | undefined;
  previousOrders: Array<Order>;
  surveys: Array<Survey>;
  chat: ChatDetails;
};
const initState: ReverieState = {
  initialized: false,
  fetching: false,
  patient: null,
  prescriptions: [],
  splashCopy: null,
  order: null,
  previousOrders: [],
  surveys: [],
  chat: {
    token: null,
    expires: null,
    started: false,
    fetching: false,
  },
};
export default function reverieReducer(state: ReverieState = initState, action: ReverieAction): ReverieState {
  switch (action.type) {
    case 'reverie/SET_INITIALIZED':
      return { ...state, initialized: true, fetching: false };

    case 'reverie/SET_FETCHING':
      return { ...state, fetching: action.fetching };

    case 'reverie/SET_PATIENT':
      return { ...state, patient: action.patient };

    case 'reverie/SET_PRESCRIPTIONS':
      return { ...state, prescriptions: action.prescriptions };

    case 'reverie/SET_SPLASH_COPY':
      return { ...state, splashCopy: action.splashCopy };

    case 'reverie/SET_ORDER':
      return { ...state, order: action.order };

    case 'reverie/SET_PREVIOUS_ORDER':
      return { ...state, previousOrders: action.previousOrders };

    case 'reverie/ADD_ITEM_SUCCESS':
      if (!state.order) return state;
      return {
        ...state,
        order: { ...state.order, items: [...state.order.items, action.item], total: state.order.total + action.price },
        fetching: false,
      };

    case 'reverie/REMOVE_ITEM_SUCCESS':
      if (!state.order) return state;
      return {
        ...state,
        order: {
          ...state.order,
          items: state.order.items.filter((i) => i.id !== action.itemId),
          total: state.order ? state.order.total - action.price : 0,
        },
        fetching: false,
      };

    case 'reverie/SELECT_PRICE_TYPE_SUCCESS':
      if (!state.order) return state;
      return {
        ...state,
        order: {
          ...state.order,
          items: state.order.items.map((i) => {
            if (i.id !== action.itemId) return i;
            return action.newItem;
          }),
          total: state.order ? state.order.total + action.priceDiff : 0,
        },
        fetching: false,
      };

    case 'reverie/SET_SELECTED_FULFILLMENT_OPTION_SUCCESS':
      return {
        ...state,
        order: { ...state.order, fulfillmentSelection: action.fulfillmentSelection },
        fetching: false,
      };

    case 'reverie/SET_ORDER_SPECIAL_INSTRUCTIONS_SUCCESS':
      return { ...state, order: { ...state.order, specialInstructions: action.specialInstructions }, fetching: false };

    case 'reverie/SUBMIT_ORDER_SUCCESS':
      return { ...state, prescriptions: [], order: null, fetching: false };

    case 'reverie/SET_AUTO_REFILL_OPTED_IN_SUCCESS':
      if (!state.order) return state;
      return {
        ...state,
        order: {
          ...state.order,
          items: state.order.items.map((i) => {
            if (i.id !== action.itemId) return i;
            return { ...i, detail: { ...i.detail, selectedAutoRefillOptedIn: action.autoRefillOptedIn } };
          }),
        },
        fetching: false,
      };

    case 'reverie/CANCEL_AUTO_REFILL_SUCCESS':
      return {
        ...state,
        prescriptions: state.prescriptions.map((i) => {
          if (i.drugDetail.medNameId !== action.medNameId) return i;
          return { ...i, refillDetails: { ...i.refillDetails, enrolledInAutorefill: false } };
        }),
        fetching: false,
      };

    case 'reverie/DELAY_AUTO_REFILL_SUCCESS':
      return {
        ...state,
        prescriptions: state.prescriptions.map((i) => {
          if (i.drugDetail.medNameId !== action.medNameId) return i;
          return { ...i, refillDetails: { ...i.refillDetails, consumerRefillDate: action.expectedDate.toISOString() } };
        }),
        fetching: false,
      };

    case 'reverie/SET_CHAT_FETCHING':
      return { ...state, chat: { ...state.chat, fetching: true } };

    case 'reverie/SET_CHAT_STARTED':
      return { ...state, chat: { ...state.chat, started: true, fetching: false } };

    case 'reverie/SET_CHAT_TOKEN':
      return { ...state, chat: { ...state.chat, token: action.token, expires: action.expires } };

    case 'reverie/GET_EXIT_TOKEN_SUCCESS':
    case 'reverie/SUBMIT_INSURANCE_INFO_SUCCESS':
    case 'reverie/SEND_VERIFY_CODE_SUCCESS':
    case 'reverie/VERIFY_PATIENT_SUCCESS':
    case 'reverie/GET_FULFILLMENT_OPTIONS_SUCCESS':
    case 'reverie/SUBMIT_SURVEY_RESPONSE_SUCCESS':
      return { ...state, fetching: false };

    case 'reverie/CANCEL_ITEM_SUCCESS':
      return { ...state, prescriptions: [], order: null, previousOrders: [] };

    case 'reverie/SET_SURVEYS':
      return { ...state, surveys: action.surveys };

    case 'reverie/SUBMIT_QUESTION_RESPONSE_SUCCESS':
      return {
        ...state,
        // TODO (aba): Refactor all single page references when support for multiple pages arrives.
        // i.e. `pages[0]`
        surveys: state.surveys.map((s) => {
          const surveyId = s.surveyTemplateId?.toString();

          if (surveyId === action.surveyId) {
            s.pages[0].elements.map((q) => {
              if (q.name === action.questionId) {
                q.defaultValue = action.value;
              }

              return q;
            });
          }

          return s;
        }),
      };

    default:
      return state;
  }
}
